export const omit = <T extends {}, U extends keyof T>(
  keys: U[],
  object: T,
): Omit<T, U> =>
  Object.fromEntries(
    Object.entries(object).filter(([key]) => !keys.includes(key as U)),
  ) as Omit<T, U>;

/**
 * Given an object, return a querystring style string (x=1&y=2&z=three)
 * @param {Object} params
 * @returns {String}
 */
export const paramsToQs = (params: Record<string, unknown>) => {
  params = params || {};
  return Object.keys(params)
    .map((k) => {
      let value = params[k];
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      return k + '=' + encodeURIComponent(value as string);
    })
    .join('&');
};

/**
 * The implementation of our deepMerge
 * @param {*} dst
 * @param {*} src
 */
const _deepMerge = (
  dst: { [key: string]: unknown },
  src: { [key: string]: unknown },
) => {
  Object.keys(src).forEach((k) => {
    const v = src[k];
    if (v !== undefined) {
      if (typeof v === 'object') {
        dst[k] = Array.isArray(v)
          ? v
          : _deepMerge(
              (dst[k] || {}) as { [key: string]: unknown },
              v as { [key: string]: unknown },
            );
      } else {
        dst[k] = v;
      }
    }
  });
  return dst;
};

/**
 * Very simple deepMerge which works for simple objects. Arrays aren't merged, the target (left) is overwritten
 * by the source (right), and if the value is undefined then the property isn't copied over. This is intentional.
 * Accepts a variable number of arguments, and merges from left-to-right.
 * eg. deepMerge({}, stuff, moreImportantStuff, mostImportantStuff)
 */
export const deepMerge = <T extends {}>(
  ...args: { [key: string]: unknown }[]
): T => {
  // @ts-ignore
  return args.reduce((acc, curr) => {
    return _deepMerge(acc, curr);
  }, args.shift() as { [key: string]: unknown });
};
